/**
 * Custom hook for authentication and user data management
 */

import { useState, useEffect, useCallback } from 'react';
import authService from '../services/authService.jsx';

export const useAuth = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  /**
   * Load user data on mount and when authentication changes
   */
  useEffect(() => {
    const loadUserData = async () => {
      try {
        setLoading(true);
        setError(null);

        if (authService.isAuthenticated()) {
          const userData = await authService.getEnhancedUserData();
          setUser(userData);
        } else {
          setUser(null);
        }
      } catch (err) {
        setError(err.message || 'Failed to load user data');
        // Fallback to cached data if API fails
        const cachedUser = authService.getCurrentUser();
        setUser(cachedUser);
      } finally {
        setLoading(false);
      }
    };

    loadUserData();

    // Listen for storage changes (login/logout in other tabs)
    const handleStorageChange = (e) => {
      if (e.key === 'auth') {
        loadUserData();
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  /**
   * Refresh user data from API
   */
  const refreshUser = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      if (authService.isAuthenticated()) {
        const userData = await authService.getEnhancedUserData(true);
        setUser(userData);
        return userData;
      } else {
        setUser(null);
        return null;
      }
    } catch (err) {
      setError(err.message || 'Failed to refresh user data');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Login user
   */
  const login = useCallback(async (email, password) => {
    try {
      setLoading(true);
      setError(null);

      const authData = await authService.login(email, password);

      // The authService.login method now automatically fetches fresh user data
      // Get the updated user data from localStorage
      const updatedUser = authService.getCurrentUser();
      setUser(updatedUser);

      return authData;
    } catch (err) {
      setError(err.message || 'Login failed');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Logout user
   */
  const logout = useCallback(async () => {
    try {
      await authService.logout();
      setUser(null);
      setError(null);
    } catch (err) {
      setUser(null);
    }
  }, []);

  /**
   * Login with Google
   */
  const loginWithGoogle = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const authData = await authService.signInWithGoogle();

      // The authService.signInWithGoogle method now automatically fetches fresh user data
      // Get the updated user data from localStorage
      const updatedUser = authService.getCurrentUser();
      setUser(updatedUser);

      return authData;
    } catch (err) {
      setError(err.message || 'Google login failed');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Register with Google
   */
  const registerWithGoogle = useCallback(async (role = 'User') => {
    try {
      setLoading(true);
      setError(null);

      const authData = await authService.signUpWithGoogle(role);

      // The authService.signUpWithGoogle method now automatically fetches fresh user data
      // Get the updated user data from localStorage
      const updatedUser = authService.getCurrentUser();
      setUser(updatedUser);

      return authData;
    } catch (err) {
      setError(err.message || 'Google registration failed');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    user,
    loading,
    error,
    isAuthenticated: !!user && authService.isAuthenticated(),
    refreshUser,
    login,
    loginWithGoogle,
    registerWithGoogle,
    logout
  };
};

/**
 * Hook for getting current user with automatic refresh
 */
export const useCurrentUser = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchUser = async () => {
      try {
        setLoading(true);
        setError(null);

        if (authService.isAuthenticated()) {
          const userData = await authService.getEnhancedUserData();
          setUser(userData);
        } else {
          setUser(null);
        }
      } catch (err) {
        setError(err.message);
        // Fallback to cached data
        const cachedUser = authService.getCurrentUser();
        setUser(cachedUser);
      } finally {
        setLoading(false);
      }
    };

    fetchUser();
  }, []);

  const refreshUser = useCallback(async () => {
    try {
      setError(null);
      const userData = await authService.fetchCurrentUser();
      setUser(userData);
      return userData;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }, []);

  return { user, loading, error, refreshUser };
};
